import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Inbox, Users, Clock, CheckCircle2, AlertCircle, Eye, ShieldOff, Search, RefreshCw
} from 'lucide-react';
import supportService, { TicketStats, UserTicketStat } from '../../services/support.service';
import authService from '../../services/auth.service';
import Table from '../Table/Table';
import CommonPagination from '../Common/CommonPagination';
import styles from './SupportTicketList.module.css';

function getInitials(name: string) {
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
}

const SupportTicketList: React.FC = () => {
    const user = authService.getUser();
    const isSuperAdmin = user?.is_superuser;
    const navigate = useNavigate();

    const [stats, setStats] = useState<TicketStats | null>(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('total');
    const [currentPage, setCurrentPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

    const loadStats = async (
        page: number,
        searchVal: string,
        status: string,
        pageSize: number,
    ) => {
        setLoading(true);
        try {
            const data = await supportService.getStats({
                search: searchVal || undefined,
                status: status !== 'total' ? status : undefined,
                page: page + 1,       // API is 1-based; ReactPaginate is 0-based
                pageSize,
            });
            setStats(data);
        } catch {
            // silent
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isSuperAdmin) return;
        loadStats(0, '', 'total', itemsPerPage);
    }, [isSuperAdmin]);

    const handleSearchChange = (val: string) => {
        setSearch(val);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setCurrentPage(0);
            loadStats(0, val, statusFilter, itemsPerPage);
        }, 350);
    };

    const handleStatClick = (status: string) => {
        const next = statusFilter === status ? 'total' : status;
        setStatusFilter(next);
        setCurrentPage(0);
        loadStats(0, search, next, itemsPerPage);
    };

    const statCards = [
        {
            title: 'Total Tickets',
            value: stats?.total ?? 0,
            icon: Inbox,
            color: 'purple',
            status: 'total',
        },
        {
            title: 'Open Tickets',
            value: stats?.open ?? 0,
            icon: AlertCircle,
            color: 'orange',
            status: 'open',
        },
        {
            title: 'In Progress',
            value: stats?.in_progress ?? 0,
            icon: Clock,
            color: 'blue',
            status: 'in_progress',
        },
        {
            title: 'Resolved',
            value: stats?.resolved ?? 0,
            icon: CheckCircle2,
            color: 'green',
            status: 'resolved',
        },
    ];

    const columns = [
        {
            key: 'user_name',
            header: 'User',
            render: (_: any, row: UserTicketStat) => (
                <div className={styles.userCell}>
                    <div className={styles.userAvatar}>{getInitials(row.user_name)}</div>
                    <div>
                        <div className={styles.userName}>{row.user_name}</div>
                        {row.user_email && <div className={styles.userEmail}>{row.user_email}</div>}
                    </div>
                </div>
            ),
        },
        {
            key: 'open',
            header: 'Open',
            render: (value: number) => (
                <span className={`${styles.statusBadge} ${styles.open}`}>{value}</span>
            ),
        },
        {
            key: 'in_progress',
            header: 'In Progress',
            render: (value: number) => (
                <span className={`${styles.statusBadge} ${styles.inProgress}`}>{value}</span>
            ),
        },
        {
            key: 'resolved',
            header: 'Resolved',
            render: (value: number) => (
                <span className={`${styles.statusBadge} ${styles.resolved}`}>{value}</span>
            ),
        },
        {
            key: 'total',
            header: 'Total',
            render: (value: number) => (
                <strong style={{ fontWeight: 700, color: '#1e293b' }}>{value}</strong>
            ),
        },
        {
            key: 'actions',
            header: 'Actions',
            render: (_: any, row: UserTicketStat) => (
                <button
                    className={`${styles.actionBtn} ${styles.view}`}
                    onClick={() => navigate(`/support-admin/user/${row.user_id}`)}
                    title="View tickets"
                >
                    <Eye size={14} />
                </button>
            ),
        },
    ];

    if (!isSuperAdmin) {
        return (
            <div className={styles.managementContent}>
                <div style={{ textAlign: 'center', padding: '80px', color: '#64748b' }}>
                    <ShieldOff size={48} style={{ marginBottom: 16, opacity: 0.4 }} />
                    <h2>Access Restricted</h2>
                    <p>Only Super Admins can view the Support dashboard.</p>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.managementContent}>
            {/* Stat Cards */}
            <div className={styles.statsGrid}>
                {statCards.map(card => (
                    <div
                        key={card.status}
                        className={`${styles.statCard} ${styles[card.color]} ${statusFilter === card.status ? styles.selected : ''}`}
                        onClick={() => handleStatClick(card.status)}
                        style={{ cursor: 'pointer' }}
                    >
                        <div className={styles.statIcon}>
                            <card.icon size={16} />
                        </div>
                        <div className={styles.statContent}>
                            <h3>{loading ? '—' : card.value}</h3>
                            <p>{card.title}</p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Table Section */}
            <div className={styles.tableSection}>
                <div className={styles.tableHeader}>
                    <h2>
                        <Users size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                        Tickets
                    </h2>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
                        {/* Search */}
                        <div style={{ position: 'relative', width: 260 }}>
                            <Search
                                size={16}
                                style={{
                                    position: 'absolute',
                                    left: 12,
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    color: '#9ca3af',
                                    pointerEvents: 'none',
                                    zIndex: 1,
                                }}
                            />
                            <input
                                type="text"
                                placeholder="Search by name or email..."
                                value={search}
                                onChange={e => handleSearchChange(e.target.value)}
                                className={styles.searchInput}
                            />
                        </div>

                        {/* Refresh */}
                        <button
                            className={styles.addBtn}
                            onClick={() => loadStats(currentPage, search, statusFilter, itemsPerPage)}
                            title="Refresh"
                        >
                            <RefreshCw size={14} />
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px 20px', color: '#64748b' }}>
                        <div className={styles.spinner} style={{ margin: '0 auto 12px' }} />
                        Loading tickets...
                    </div>
                ) : (
                    <Table
                        columns={columns}
                        data={stats?.by_user ?? []}
                        maxHeight="calc(100vh - 360px)"
                    />
                )}

                <CommonPagination
                    show={(stats?.total_users ?? 0) > 0}
                    pageCount={Math.ceil((stats?.total_users ?? 0) / itemsPerPage)}
                    currentPage={currentPage}
                    totalItems={stats?.total_users ?? 0}
                    itemsPerPage={itemsPerPage}
                    onPageChange={(data) => {
                        setCurrentPage(data.selected);
                        loadStats(data.selected, search, statusFilter, itemsPerPage);
                    }}
                    onItemsPerPageChange={(items) => {
                        setItemsPerPage(items);
                        setCurrentPage(0);
                        loadStats(0, search, statusFilter, items);
                    }}
                />
            </div>
        </div>
    );
};

export default SupportTicketList;
